import { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { RadioGroup, Tab } from '@headlessui/react';
import { LineItem } from '@Types/cart/LineItem';
import { Category } from '@Types/product/Category';
import { Money } from '@Types/product/Money';
import { Product } from '@Types/product/Product';
import { Review } from '@Types/product/Review';
import { Variant } from '@Types/product/Variant';
import { FacebookShareButton, LinkedinShareButton, PinterestShareButton } from 'react-share';
import Breadcrumb from 'components/commercetools-ui/breadcrumb';
import Price from 'components/commercetools-ui/price';
import { Rating } from 'components/common/Rating';
import FacebookIcon from 'components/icons/social-media/facebook';
import LinkedinIcon from 'components/icons/social-media/linkedin';
import PinterestIcon from 'components/icons/social-media/pinterest';
import { FullPageWidthWrapper } from 'components/tailgrids-ui/full-bleed';
import { useFormat } from 'helpers/hooks/useFormat';
import { useProductStore } from 'helpers/stores/productStore';
import { CustomImage } from 'lib/customImage';
import { useAccount, useWishlist } from 'frontastic';
import { ReviewSection } from './ReviewSection';
import { useQuery, gql } from '@apollo/client';
import { BannerAdapter } from 'components/tailgrids-ui/strapi/Banner/BannerAdapter';
import { strapiConfig } from 'helpers/strapiConfig';
import parse from 'html-react-parser';
import { ReferenceLink } from 'helpers/reference';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export interface ProductDetailsProps {
  product: UIProduct;
  onAddToCart: (variant: Variant, quanitity?: number, kitSelected?: string) => Promise<void>;
  onAddToWishlist: () => void;
  variant: Variant;
  onChangeVariantIdx: (idx: number) => void;
  quickBuyEnabled?: boolean;
}

export type UIProduct = {
  productId: string;
  name: string;
  categories: Category[];
  variants: Variant[];
  price?: Money;
  images?: UIImage[];
  colors?: UIColor[];
  sizes?: UISize[];
  lengths?: UILength[];
  waists?: UIWaist[];
  description: string;
  details?: UIDetail[];
  isOnWishlist?: boolean;
  _url: string;
  mainCategory?: string;
  productRelevance?: number;
  loggedInOnly?: boolean;
  customerGroupTarget?: any;
  reviews?: Review[];
  reviewStats?: Product['reviewStats'];
  hasReviewed: boolean;
  reviewMessage?: Record<string, string>;
  hasBought: boolean;
  moderateReviews: boolean;
};

interface UIImage {
  id?: string;
  src?: string;
  alt?: string;
}
export interface UIColor {
  name?: string;
  key?: string;
  bgColor?: string;
  selectedColor?: string;
}
export interface UISize {
  label: string;
  key: string;
}
interface UIDetail {
  name: string;
  items: string[];
}
export interface UIWaist {
  label: string;
  key: string;
}
export interface UILength {
  label: string;
  key: string;
}

export default function ProductDetails({
  product,
  onAddToCart,
  onAddToWishlist,
  variant,
  onChangeVariantIdx,
  quickBuyEnabled,
}: ProductDetailsProps) {
  //next/router
  const router = useRouter();
  const [isSizeProduct, setIsSizeProduct] = useState<boolean>(true);
  const { removeLineItem, data } = useWishlist();
  const [quantity, setQuantity] = useState<number>(1);
  const { account } = useAccount();

  //i18n messages
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  //Variant attributes
  const selectedColor = useMemo<UIColor | undefined>(
    () => product?.colors?.find((c) => c.key === variant?.attributes?.color?.key) ?? product?.colors?.[0],
    [product, variant],
  );

  const selectedSize = useMemo<UISize>(
    // () => product?.sizes?.find((s) => s?.key === variant?.attributes?.commonSize?.key) ?? product?.sizes?.[0],
    () =>
      // product?.sizes?.find((s) => s?.key === variant?.attributes?.commonSize) ??
      product?.variants?.find((v) => v.isOnStock)?.attributes?.commonSize ??
      product?.variants[0]?.attributes?.commonSize,
    [product, variant],
  );

  const selectedWaist = useMemo<UIWaist>(
    () => product?.waists?.find((w) => w.key === variant?.attributes?.waist) ?? product?.waists?.[0],
    [product, variant],
  );

  const selectedLength = useMemo<UILength>(
    () => product?.lengths?.find((w) => w.key === variant?.attributes?.length) ?? product?.lengths?.[0],
    [product, variant],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [added, setAdded] = useState<boolean>(false);
  const [productsSelected, setProductsSelected] = useState<Record<string, Product>>({});
  const [chosenSize, setChosenSize] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const { size: savedSize, updateSize: updateSavedSize } = useProductStore();

  const getSelectedProducts = (): string => {
    if (Object.keys(productsSelected).length === 0) return undefined;

    const selected = Object.keys(productsSelected).map((category) => {
      const product = productsSelected[category];
      return `${category}:${product.productId}`;
    });

    return selected.join(',');
  };

  const selectProduct = (category: string, product: Product) => {
    setProductsSelected((prev) => ({ ...prev, [category]: product }));
  };

  const selectSize = (size: UISize) => {
    onChangeVariantIdx(
      product?.variants?.findIndex(
        (variant) =>
          variant?.attributes?.color?.key === selectedColor.key && variant?.attributes?.commonSize?.key === size?.key,
      ) ?? 0,
    );
  };

  const selectWaist = (waist: UIWaist) => {
    onChangeVariantIdx(
      product?.variants?.findIndex(
        (variant) => variant?.attributes?.color?.key === selectedColor.key && variant?.attributes?.waist === waist.key,
      ) ?? 0,
    );
  };

  const selectLength = (length: UILength) => {
    onChangeVariantIdx(
      product?.variants?.findIndex(
        (variant) =>
          variant?.attributes?.color?.key === selectedColor.key && variant?.attributes?.length === length.key,
      ) ?? 0,
    );
  };

  const selectColor = (color: UIColor) => {
    onChangeVariantIdx(
      product?.variants?.findIndex(
        (variant) =>
          // variant?.attributes?.color?.key === color.key && variant?.attributes?.commonSize?.key === selectedSize.key,
          variant?.attributes?.color?.key === color.key,
      ) ?? 0,
    );
  };

  const variantExistsFor = useCallback(
    (color: UIColor, size: UISize) => {
      return !!product?.variants?.find(
        (v) => v?.attributes?.color?.key === color?.key && v?.attributes?.commonSize?.key === size?.key,
      );
    },
    [product],
  );

  const wVariantExistsFor = useCallback(
    (color: UIColor, waist: UIWaist) => {
      return !!product?.variants?.find(
        (v) => v?.attributes?.color?.key === color?.key && v?.attributes?.waist === waist?.key,
      );
    },
    [product],
  );

  const lVariantExistsFor = useCallback(
    (color: UIColor, length: UILength, waist: UIWaist) => {
      return !!product?.variants?.find(
        (v) =>
          v?.attributes?.color?.key === color?.key &&
          v?.attributes?.length === length?.key &&
          v?.attributes?.waist === waist?.key,
      );
    },
    [product],
  );

  useEffect(() => {
    setSelectedVariant(product.variants.find((v) => v?.isOnStock && v?.attributes?.commonSize?.key === chosenSize));
  }, [chosenSize]);

  const hasOneSize = useMemo(() => {
    return (
      Array.from(
        new Set(
          product.variants
            .filter((variant) => variant?.attributes?.color?.key === selectedColor?.key)
            .map((item) => item?.attributes?.commonSize?.key),
        ),
      ).length === 1
    );
  }, [selectedColor]);

  const handleAddToCart = async () => {
    if (loading || !variant.isOnStock) return;
    if (selectedVariant !== null && (selectedVariant?.attributes?.commonSize?.key === chosenSize || hasOneSize)) {
      setLoading(true);
      await onAddToCart(variant, quantity, getSelectedProducts());
      setLoading(false);
      setAdded(true);
    }
  };

  const handleQuickBuy = async () => {
    await onAddToCart(variant, 2, getSelectedProducts());
    router.push('/checkout');
  };

  const categoryPaths = useMemo<any>(
    () =>
      product?.categories?.find((element) => element?.categoryId === product?.mainCategory)?.slug ??
      product?.categories[0]?.slug ??
      '',
    [product],
  );

  const breadcrumbNavigation = useMemo<any>(
    () =>
      product?.categories?.find((element) => element?.categoryId === product?.mainCategory)?.breadcrumbNavigation ??
      product?.categories[0]?.breadcrumbNavigation ??
      '',
    [product],
  );

  useEffect(() => {
    if (added) {
      setTimeout(() => {
        setAdded(false);
      }, 1000);
    }
  }, [added]);

  useEffect(() => {
    setIsSizeProduct(product.categories.filter((c) => c.categoryId === 'c25').length === 0);
  }, [product.categories]);

  useEffect(() => {
    if (
      product?.variants?.filter(
        (variant) =>
          variant?.attributes?.commonSize?.key === savedSize &&
          variant?.attributes?.color?.key === selectedColor?.key &&
          variant?.isOnStock,
      ).length > 0
    ) {
      setChosenSize(savedSize);
    }
  }, []);

  const isNewProduct = useMemo(() => {
    return !!product?.categories?.find(
      (category) => category.slug.includes('new') || category.slug.includes('neu') || category.slug.includes('nouveau'),
    );
  }, []);

  const isOnSale = useMemo(() => {
    return !!product?.categories?.find((category) => category.slug.includes('sale') || category.slug.includes('vente'));
  }, []);

  const showCharacters = useCallback((str) => {
    const mapChar = {
      U2D: '-',
      U20: ' ',
      U26: ' & ',
    };
    return str.replace(/(?:U2D|U20|U26)/gi, (matched) => mapChar[matched]);
  }, []);

  const onRemoveFromWishList = async (item: LineItem) => {
    await removeLineItem(item.lineItemId);
  };

  const sortOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];

  const colorSelectorHandler = (e: UIColor) => {
    selectColor(e);
    if (
      product?.variants?.filter(
        (variant) =>
          variant?.attributes?.color?.key === e?.key &&
          variant?.attributes?.commonSize?.key === savedSize &&
          variant?.isOnStock,
      ).length > 0
    ) {
      setChosenSize(savedSize);
    } else {
      setChosenSize(null);
    }
  };

  const sizeSelectorHandler = (size: string) => {
    if (
      product?.variants?.filter(
        (variant) =>
          variant?.attributes?.color?.key === selectedColor?.key &&
          variant?.attributes?.commonSize?.key === size &&
          variant?.isOnStock,
      ).length > 0
    ) {
      setChosenSize(size);
      updateSavedSize(size);
    } else if (
      product?.variants?.filter(
        (variant) =>
          variant?.attributes?.color?.key === selectedColor?.key &&
          // variant?.attributes?.commonSize?.key === size &&
          !variant?.isOnStock,
      ).length > 0
    ) {
      setChosenSize('notAvailable');
    } else {
      setChosenSize(null);
    }
  };

  const GET_PAGES = gql`
    query ProductContent($id: String) {
      productContents(filters: { productId: { eq: $id } }, locale: "${router.locale}")  {
        data {
          id
          attributes {
            productId
            banner {
              __typename
              id
              title
              description
              customerGroups
              image {
                data {
                  attributes {
                    url
                  }
                }
              }
              buttonText
              buttonUrl
              targetBlank
            }
            info {
              __typename
              title
              description
              customerGroups
              image {
                data {
                  attributes {
                    url
                  }
                }
              }
              buttonText
              buttonUrl
              targetBlank
            }
          }
        }
      }
    }
  `;

  const { data: strapiData } = useQuery(GET_PAGES, {
    variables: { id: product.productId },
  });

  const isEmptyObject = (object) => Object.values(object).every((x) => x === null || x === '');
  const selectedLanguage = router.locale.split('-')[0];

  const customerGroups = strapiData?.productContents?.data[0]?.attributes?.info?.customerGroups;

  return (
    <FullPageWidthWrapper>
      <section className="mx-auto max-w-[1280px] bg-white px-4 pb-20 pt-10 font-urbanist sm:px-10 lg:px-4">
        <div className="container mx-auto">
          {strapiData?.productContents?.data?.length > 0 &&
            strapiData?.productContents?.data[0]?.attributes?.banner && (
              <BannerAdapter
                customerGroups={strapiData?.productContents?.data[0]?.attributes?.banner?.customerGroups}
                title={strapiData?.productContents?.data[0]?.attributes?.banner?.title}
                description={strapiData?.productContents?.data[0]?.attributes?.banner?.description}
                image={strapiData?.productContents?.data[0]?.attributes?.banner?.image}
                buttonText={strapiData?.productContents?.data[0]?.attributes?.banner?.buttonText}
                buttonUrl={strapiData?.productContents?.data[0]?.attributes?.banner?.buttonUrl}
                targetBlank={strapiData?.productContents?.data[0]?.attributes?.banner?.targetBlank}
                isFirstElement={true}
              />
            )}
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/2 xl:w-7/12">
              <Tab.Group>
                <div className="flex flex-col-reverse">
                  {/* Image selector */}
                  <div className="mx-auto mt-6 hidden w-full max-w-2xl sm:block lg:max-w-none">
                    <Tab.List className="grid grid-cols-3 gap-6">
                      {product?.images?.map((image, idx) => (
                        <Tab
                          key={image.id}
                          className="relative flex h-52 cursor-pointer items-center justify-center rounded bg-white text-sm font-medium uppercase text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-white/50 focus:ring-offset-4"
                          onClick={() => setSelectedImageIndex(idx)}
                        >
                          {({ selected }) => {
                            if (selected) {
                              setSelectedImageIndex(idx);
                            }
                            return (
                              <>
                                <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded border border-neutral-200">
                                  {/* <Image
                                    loader={({ src }) => src}
                                    layout="fill"
                                    objectFit="contain"
                                    src={image.src}
                                    alt=""
                                    // className="h-full w-full object-center"
                                    className={classNames(
                                      selected ? 'opacity-60' : 'opacity-100',
                                      'pointer-events-none absolute h-full w-full object-center',
                                    )}
                                  /> */}
                                  <CustomImage imgSrc={image.src} width={200} height={200} />
                                </div>
                              </>
                            );
                          }}
                        </Tab>
                      ))}
                    </Tab.List>
                  </div>
                  <Tab.Panels className="aspect-w-1 aspect-h-1 relative max-w-[640px] rounded">
                    {product?.images?.map((image) => (
                      <Tab.Panel key={image.id} className="absolute top-0 left-0 flex items-center justify-center">
                        {/* <Image
                          loader={({ src }) => src}
                          layout="fill"
                          objectFit="contain"
                          src={image.src}
                          alt={image.alt}
                          className="w-full rounded object-center"
                        /> */}
                        <CustomImage imgSrc={image.src.replace('medium', 'large')} width={640} height={670} />
                        <div className="absolute left-4 top-4 flex items-center justify-center gap-2">
                          {isNewProduct && (
                            <div className="flex items-center justify-center gap-1 rounded bg-secondary px-[10px] py-1 text-white">
                              <p className="text-sm font-normal">
                                {formatProductMessage({ id: 'new', defaultMessage: 'New' })}
                              </p>
                              <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.1937 4.98125L7.79997 4.45625L6.26247 1.23125C6.14997 1.00625 5.84997 1.00625 5.73747 1.23125L4.19997 4.475L0.82497 4.98125C0.58122 5.01875 0.48747 5.3375 0.67497 5.50625L3.13122 8.0375L2.54997 11.5812C2.51247 11.825 2.75622 12.0312 2.98122 11.8813L6.03747 10.2125L9.07497 11.8813C9.28122 11.9938 9.54372 11.8062 9.48747 11.5812L8.90622 8.0375L11.3625 5.50625C11.5125 5.3375 11.4375 5.01875 11.1937 4.98125Z"
                                  fill="white"
                                />
                              </svg>
                            </div>
                          )}

                          {isOnSale && (
                            <div className="flex items-center justify-center gap-1 rounded bg-[#E77474] px-[10px] py-1 text-white">
                              <p className="text-sm font-normal">
                                {formatProductMessage({ id: 'sale', defaultMessage: 'Sale' })}
                              </p>
                              <svg
                                width="14"
                                height="15"
                                viewBox="0 0 14 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_211_184)">
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.2601 1.20969C5.42417 1.0923 5.641 1.07931 5.81792 1.17627C6.36504 1.47613 7.11161 2.01577 7.66876 2.89761C8.23232 3.78957 8.58064 4.9988 8.37038 6.58826C8.36791 6.60694 8.36706 6.62081 8.367 6.63083C8.39186 6.63517 8.4391 6.63758 8.51054 6.6164C8.71512 6.55576 9.01409 6.31551 8.9819 5.74971C8.96899 5.52283 9.10352 5.3134 9.31522 5.2308C9.52692 5.1482 9.76773 5.21117 9.91189 5.38684C10.6705 6.31124 11.5449 7.85631 11.6287 9.4849C11.6712 10.3114 11.5097 11.168 11.0168 11.9625C10.5246 12.7559 9.72553 13.4494 8.55615 13.9915C8.34078 14.0913 8.08522 14.0342 7.93283 13.8522C7.78043 13.6702 7.76912 13.4086 7.90524 13.2141C8.1604 12.8496 8.49801 11.882 7.78262 10.9877C7.77356 10.9764 7.76498 10.9647 7.75689 10.9527C7.72895 10.9111 7.70497 10.8827 7.66755 10.8383C7.64267 10.8088 7.61184 10.7722 7.56996 10.7201C7.4816 10.61 7.37428 10.4625 7.29508 10.2655C7.24412 10.1387 7.20934 10.0035 7.19191 9.85713C6.91137 10.0857 6.63847 10.368 6.42307 10.6986C5.9921 11.3601 5.78034 12.2249 6.22345 13.3179C6.30382 13.5162 6.25587 13.7433 6.10224 13.8922C5.94861 14.041 5.72003 14.0818 5.52441 13.9952C4.57229 13.5738 3.33288 12.6562 2.75581 11.2397C2.16059 9.77869 2.32039 7.91545 3.94633 5.74752L3.94633 5.74752C4.52866 4.9711 5.35445 3.48153 5.0484 1.72687C5.01373 1.52812 5.09602 1.32709 5.2601 1.20969ZM8.36938 6.65336C8.36922 6.65356 8.3686 6.65219 8.36804 6.6487C8.36927 6.65142 8.36955 6.65317 8.36938 6.65336ZM6.1605 2.71032C6.09608 4.35064 5.31814 5.66846 4.78633 6.37753C3.32844 8.32138 3.30264 9.79896 3.72821 10.8436C3.99146 11.4897 4.44619 12.0175 4.93744 12.4129C4.87354 11.5264 5.13335 10.7547 5.54332 10.1254C6.126 9.23111 7.00047 8.63589 7.63632 8.3527C7.82495 8.26869 8.04531 8.30305 8.1994 8.4405C8.35349 8.57796 8.41269 8.79297 8.35069 8.98993C8.17683 9.54219 8.22822 9.77161 8.26929 9.87379C8.29316 9.93314 8.32874 9.98799 8.38866 10.0626C8.39893 10.0754 8.41339 10.0927 8.4305 10.1131C8.48172 10.1743 8.55678 10.264 8.61494 10.3474C9.15478 11.0302 9.2986 11.7402 9.25662 12.3549C9.64189 12.0533 9.92326 11.7334 10.1246 11.409C10.4869 10.8249 10.6135 10.1881 10.5801 9.53883C10.5329 8.62096 10.1655 7.69679 9.71826 6.94073C9.48965 7.28539 9.16023 7.51898 8.80895 7.62311C8.48506 7.71911 8.09695 7.71397 7.77832 7.50889C7.42502 7.28151 7.27217 6.88361 7.32945 6.45057C7.50844 5.09747 7.20954 4.13658 6.78109 3.45844C6.5948 3.16359 6.38 2.91605 6.1605 2.71032Z"
                                    fill="white"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_211_184">
                                    <rect width="14" height="14" fill="white" transform="translate(0 0.5)" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded bg-white">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className={`h-6 w-6 cursor-pointer ${
                              product.isOnWishlist ? 'fill-black-500' : 'fill-white'
                            } transition duration-150 ease-out hover:fill-black-500`}
                            onClick={
                              !product.isOnWishlist
                                ? onAddToWishlist
                                : () =>
                                    onRemoveFromWishList(
                                      data.lineItems.find((lineItem) => lineItem.productId === product.productId),
                                    )
                            }
                            data-testid="wishlist-action"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                            />
                          </svg>
                        </div>
                      </Tab.Panel>
                    ))}
                  </Tab.Panels>
                </div>
              </Tab.Group>
            </div>

            <div className="w-full md:w-1/2 md:pl-4 lg:pl-0 xl:w-5/12">
              <div className="mb-6 mt-8 flex flex-row gap-5 lg:mt-0">
                <div className="flex w-full justify-between gap-[10px]">
                  <Breadcrumb Separator={'/'} SearchIcon={false}>
                    {!isEmptyObject(breadcrumbNavigation) && breadcrumbNavigation[selectedLanguage] !== ''
                      ? breadcrumbNavigation[selectedLanguage]
                          .split('-')
                          .slice(0, 3)
                          .map((item, idx) => {
                            return (
                              <Link
                                href={categoryPaths
                                  .split('-')
                                  .slice(0, idx + 1)
                                  .join('-')}
                                key={`${item}-${idx}`}
                              >
                                {item}
                              </Link>
                            );
                          })
                      : categoryPaths
                          .split('-')
                          .slice(0, 3)
                          .map((item, idx) => {
                            return (
                              <Link
                                href={categoryPaths
                                  .split('-')
                                  .slice(0, idx + 1)
                                  .join('-')}
                                key={`${item}-${idx}`}
                              >
                                {showCharacters(item)}
                              </Link>
                            );
                          })}
                  </Breadcrumb>
                </div>
              </div>

              <h2 className="mb-5 text-2xl font-bold text-black sm:text-3xl md:text-4xl lg:text-3xl">
                {product?.name}
              </h2>

              {product.description && (
                <p className="mb-6 text-base font-medium text-[#808080]">{product.description}</p>
              )}
              <span className="mb-2 flex items-center gap-3">
                <Rating value={product.reviewStats?.average} />({product?.reviewStats?.count ?? 0})
              </span>

              <div className="xs:flex mb-10 flex-wrap justify-between">
                <div className="xs:text-right">
                  <span className="mb-1 flex items-center">
                    {variant.discountedPrice ? (
                      <>
                        <Price price={variant.discountedPrice} className="text-lg font-semibold text-gray-900" />
                        <Price
                          price={product?.price}
                          className="pl-6 text-sm font-medium text-[#808080] line-through decoration-[#808080] decoration-1"
                        />
                      </>
                    ) : (
                      <Price
                        price={product?.price}
                        className={`${
                          variant.discountedPrice ? 'line-through' : ''
                        } text-lg font-semibold text-gray-900`}
                      />
                    )}
                  </span>
                </div>
                <p className="text-xs font-normal text-[#808080]">
                  {formatProductMessage({ id: 'vat.included', defaultMessage: 'incl. VAT' })}
                </p>
              </div>

              {((customerGroups?.length > 0 && customerGroups?.includes(account?.customerGroup?.id)) ||
                customerGroups === null) &&
                strapiData?.productContents?.data?.length > 0 &&
                strapiData?.productContents?.data[0]?.attributes?.info && (
                  <div
                    className="relative my-6 mx-auto rounded bg-[#102B33] bg-cover bg-no-repeat px-4 py-4 sm:px-10 lg:px-4"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4),rgba(0, 0, 0, 0.4)) , url(${strapiConfig.url}${strapiData?.productContents?.data[0]?.attributes?.info?.image?.data?.attributes?.url})`,
                    }}
                  >
                    <div className="flex w-full flex-col items-start justify-center gap-2 py-2 text-left">
                      {strapiData?.productContents?.data[0]?.attributes?.info?.title && (
                        <h5 className="text-lg font-semibold text-white sm:text-[20px]">
                          {strapiData?.productContents?.data[0]?.attributes?.info?.title}
                        </h5>
                      )}
                      {strapiData?.productContents?.data[0]?.attributes?.info?.description && (
                        <p className="text-base font-normal text-white">
                          {parse(strapiData?.productContents?.data[0]?.attributes?.info?.description)}
                        </p>
                      )}
                      {strapiData?.productContents?.data[0]?.attributes?.info?.buttonUrl &&
                        strapiData?.productContents?.data[0]?.attributes?.info?.buttonText && (
                          <ReferenceLink
                            target={{
                              link: strapiData?.productContents?.data[0]?.attributes?.info?.buttonUrl,
                              type: 'link',
                              openInNewWindow: strapiData?.productContents?.data[0]?.attributes?.info?.targentBlank,
                            }}
                            className="mt-2 cursor-pointer rounded bg-primary py-2 px-4 text-white hover:bg-[#33B8A1] disabled:cursor-auto disabled:bg-[#EDEDED] disabled:text-[#808080]"
                          >
                            <span className="text-white">
                              {strapiData?.productContents?.data[0]?.attributes?.info?.buttonText}
                            </span>
                          </ReferenceLink>
                        )}
                    </div>
                  </div>
                )}

              <div>
                {product?.colors.filter((color) => color.name !== undefined).length > 0 && (
                  <div>
                    <h3 className="mb-4 text-sm font-medium text-black">
                      {formatProductMessage({ id: 'color.select', defaultMessage: 'Select color' })}
                    </h3>
                    <div className="mb-8 flex items-center">
                      <RadioGroup value={selectedColor} onChange={(e) => colorSelectorHandler(e)}>
                        {/* <RadioGroup.Label className="sr-only">Choose a color</RadioGroup.Label> */}
                        <div className="relative flex items-center space-x-3" data-testid="productDetailsColorSelector">
                          {product?.colors?.map((color) => (
                            <RadioGroup.Option
                              key={color.name}
                              value={color}
                              className={({ active, checked }) =>
                                classNames(
                                  (active && checked) || selectedColor?.key === color.key
                                    ? `${color.selectedColor}`
                                    : '',
                                  'relative flex cursor-pointer items-center justify-center p-0.5 focus:outline-none',
                                )
                              }
                            >
                              {selectedColor?.key === color.key && (
                                <div className="absolute left-0 flex w-full items-center justify-center">
                                  <svg
                                    width="14"
                                    height="8"
                                    viewBox="0 0 14 8"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M12.7522 0.564572C12.6184 0.439873 12.4082 0.439873 12.2744 0.564572L5.29803 6.87075C5.24069 6.92419 5.16424 6.92419 5.1069 6.87075L1.64741 3.73548C1.51361 3.61078 1.30337 3.61078 1.16958 3.73548C1.03578 3.86017 1.03578 4.05613 1.16958 4.18083L4.62907 7.3161C4.78198 7.45862 4.99222 7.52987 5.18335 7.52987C5.3936 7.52987 5.58473 7.45862 5.73764 7.3161L12.714 1.00992C12.8669 0.885225 12.8669 0.68927 12.7522 0.564572Z"
                                      fill={color?.key?.toLowerCase() === 'white' ? 'black' : 'white'}
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M13.0863 0.23222L13.0986 0.245562C13.3863 0.558375 13.3828 1.06122 13.0224 1.36506L6.05559 7.6626C5.81122 7.88897 5.50465 8 5.18336 8C4.88682 8 4.55941 7.89258 4.31092 7.6624L0.848699 4.52465C0.515257 4.21387 0.515288 3.70154 0.84873 3.39077C1.16229 3.09852 1.65073 3.09762 1.96536 3.38808L5.20276 6.32207L11.9567 0.216909C12.2713 -0.073284 12.7596 -0.0722998 13.073 0.219862L13.0863 0.23222ZM1.64741 3.73548C1.51361 3.61078 1.30337 3.61078 1.16958 3.73548C1.03578 3.86017 1.03578 4.05613 1.16958 4.18083L4.62907 7.3161C4.78198 7.45862 4.99222 7.52987 5.18335 7.52987C5.3936 7.52987 5.58473 7.45862 5.73764 7.3161L12.714 1.00992C12.8669 0.885225 12.8669 0.68927 12.7522 0.564572C12.6184 0.439873 12.4082 0.439873 12.2744 0.564572L5.29803 6.87075C5.24069 6.92419 5.16424 6.92419 5.1069 6.87075L1.64741 3.73548Z"
                                      fill={color?.key?.toLowerCase() === 'white' ? 'black' : 'white'}
                                    />
                                  </svg>
                                </div>
                              )}
                              <span
                                aria-hidden="true"
                                className={`
                                ${
                                  color?.key?.toLowerCase() === 'multicolored'
                                    ? 'bg-gradient-to-r from-cyan-800 to-amber-300'
                                    : color.bgColor
                                }
                                h-8 w-8 rounded
                                ${color?.key?.toLowerCase() === 'white' ? 'border-[1px] border-[#E7E7E7]' : ''}`}
                              />
                            </RadioGroup.Option>
                          ))}
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}

                {product.sizes?.length && product.sizes.length > 0 && (
                  <div className="mb-12">
                    <div className="mb-4 flex w-full items-center justify-between">
                      <h3 className="text-sm font-medium text-black">
                        {!hasOneSize
                          ? formatProductMessage({ id: 'size.select', defaultMessage: 'Select size' })
                          : formatProductMessage({
                              id: 'size.hasOneSize',
                              defaultMessage: 'This item has one size only',
                            })}
                      </h3>
                      <h3 className="cursor-pointer text-sm font-medium text-black underline">
                        {formatProductMessage({ id: 'size.table', defaultMessage: 'Size table' })}
                      </h3>
                    </div>

                    <RadioGroup value={selectedSize} onChange={(e) => selectSize(e)} className="block">
                      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6" data-testid="productDetailsSizeSelector">
                        {/* {product.sizes
                        .filter((size) => variantExistsFor(selectedColor, size)) */}
                        {!hasOneSize &&
                          Array.from(
                            new Set(
                              product?.variants
                                //  ?.filter((variant) => variant?.attributes?.color?.key === selectedColor?.key)
                                ?.map((item) => item?.attributes?.commonSize?.key),
                            ),
                          )
                            ?.filter((size) => size !== undefined)
                            .sort(function (a, b) {
                              return sortOrder.indexOf(a) - sortOrder.indexOf(b);
                            })
                            ?.map((size, idx) => (
                              <div
                                onClick={() => sizeSelectorHandler(size)}
                                key={size}
                                // data-testid={`size-${size.toLowerCase()}`}
                              >
                                <RadioGroup.Option
                                  // key={size}
                                  value={{ key: size }}
                                  className={({ active, checked, disabled }) =>
                                    classNames(
                                      active || chosenSize === size || checked
                                        ? 'border-0 bg-primary text-white'
                                        : disabled
                                        ? 'cursor-auto bg-[#EDEDED] text-[#808080]'
                                        : 'text-gray-900 hover:bg-neutral-200',
                                      'flex cursor-pointer justify-center rounded border-[1px] border-[#E7E7E7] py-2 px-6 text-center font-light transition duration-150 ease-out',
                                    )
                                  }
                                  disabled={
                                    product?.variants?.filter(
                                      (variant) =>
                                        variant?.attributes?.color?.key === selectedColor?.key &&
                                        variant?.attributes?.commonSize?.key === size &&
                                        variant?.isOnStock,
                                    ).length === 0
                                  }
                                >
                                  {({ disabled }) => (
                                    <button
                                      type="button"
                                      className={`${disabled ? 'cursor-auto' : 'cursor-pointer'}`}
                                      data-testid={disabled ? `out-of-stock-size-${idx}` : `available-size-${idx}`}
                                    >
                                      {size.toUpperCase()}
                                    </button>
                                  )}
                                </RadioGroup.Option>
                              </div>
                            ))}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                <div className="mb-12 flex flex-col items-start justify-start gap-4 lg:flex-row lg:items-end">
                  <div>
                    <p className="mb-2 text-base font-medium text-black">
                      {formatProductMessage({ id: 'quantity', defaultMessage: 'Quantity' })}
                    </p>
                    <div className="inline-flex items-center rounded border border-[#e7e7e7] text-base font-medium text-black">
                      <span
                        className="cursor-pointer select-none py-2 px-4"
                        onClick={() => setQuantity((prev) => (prev - 1 > 0 ? prev - 1 : prev))}
                      >
                        -
                      </span>
                      <span className="py-2 px-4">{quantity}</span>
                      <span
                        className="cursor-pointer select-none py-2 px-4"
                        onClick={() => setQuantity((prev) => prev + 1)}
                      >
                        +
                      </span>
                    </div>
                  </div>
                  <div className="align-stretch flex w-full flex-col gap-4">
                    {quickBuyEnabled && (
                      <button
                        type="button"
                        onClick={handleQuickBuy}
                        className={classNames(
                          'flex w-full flex-1 items-center justify-center rounded border border-transparent bg-primary py-3 px-8  text-base font-bold text-white transition duration-150 ease-out hover:bg-opacity-90 focus:bg-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-gray-50 disabled:bg-[#808080]',
                        )}
                        disabled={!variant.isOnStock}
                      >
                        Buy Now
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      className={classNames(
                        'flex w-full items-center justify-center rounded bg-primary py-3 px-10 text-center text-base font-bold text-white hover:bg-opacity-90 disabled:bg-[#EDEDED] disabled:text-[#808080]',
                        !quickBuyEnabled
                          ? 'flex w-full items-center justify-center rounded bg-primary py-3 px-10 text-center text-base font-bold text-white hover:bg-opacity-90'
                          : 'flex w-full items-center justify-center rounded bg-primary py-3 px-10 text-center text-base font-bold text-white hover:bg-opacity-90 hover:fill-white hover:text-white',
                      )}
                      // disabled={!variant.isOnStock}
                      data-testid="addToCart"
                      disabled={
                        !product.variants.find((v) =>
                          !hasOneSize
                            ? v?.attributes?.commonSize?.key === chosenSize &&
                              v?.attributes?.color?.key === selectedColor?.key &&
                              v?.isOnStock
                            : v?.attributes?.color === undefined
                            ? v?.isOnStock
                            : v?.attributes?.color?.key === selectedColor?.key && v?.isOnStock,
                        )
                      }
                    >
                      {!loading && !added && (
                        <>
                          {
                            // variant?.isOnStock
                            product.variants.find((v) =>
                              !hasOneSize
                                ? v?.attributes?.commonSize?.key === chosenSize &&
                                  v?.attributes?.color?.key === selectedColor?.key &&
                                  v?.isOnStock
                                : v?.attributes?.color === undefined
                                ? v?.isOnStock
                                : v?.attributes?.color?.key === selectedColor?.key && v?.isOnStock,
                            )
                              ? formatProductMessage({ id: 'bag.add', defaultMessage: 'Add to bag' })
                              : !hasOneSize && chosenSize === null
                              ? formatProductMessage({ id: 'chooseOptions', defaultMessage: 'Choose Options' })
                              : formatProductMessage({ id: 'outOfStock', defaultMessage: 'Out of stock' })
                          }
                        </>
                      )}

                      {loading && (
                        <svg
                          className="h-6 w-6 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 25 25"
                          fill="white"
                        >
                          <path d="M8,8.5A3.5,3.5,0,1,1,4.5,5,3.5,3.5,0,0,1,8,8.5ZM4.5,14A3.5,3.5,0,1,0,8,17.5,3.5,3.5,0,0,0,4.5,14Zm16-2A3.5,3.5,0,1,0,17,8.5,3.5,3.5,0,0,0,20.5,12Zm0,2A3.5,3.5,0,1,0,24,17.5,3.5,3.5,0,0,0,20.5,14Zm-8,4A3.5,3.5,0,1,0,16,21.5,3.5,3.5,0,0,0,12.5,18Zm0-18A3.5,3.5,0,1,0,16,3.5,3.5,3.5,0,0,0,12.5,0Z" />
                        </svg>
                      )}
                      {!loading && added && (
                        <svg className="h-6 w-6" viewBox="0 0 80.588 61.158" fill="white">
                          <path
                            d="M29.658,61.157c-1.238,0-2.427-0.491-3.305-1.369L1.37,34.808c-1.826-1.825-1.826-4.785,0-6.611
                     c1.825-1.826,4.786-1.827,6.611,0l21.485,21.481L72.426,1.561c1.719-1.924,4.674-2.094,6.601-0.374
                     c1.926,1.72,2.094,4.675,0.374,6.601L33.145,59.595c-0.856,0.959-2.07,1.523-3.355,1.56C29.746,61.156,29.702,61.157,29.658,61.157z
                     "
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="mb-6 space-y-4">
                  <TabItem
                    title={formatProductMessage({ id: 'product.returns', defaultMessage: 'Shipping and Returns' })}
                    details="Lorem ipsum dolor sit amet consectetur adipisicing elit. Earum animi pariatur hic fuga quidem labore repudiandae sequi, molestias provident quisquam!"
                  />
                  <TabItem
                    title={formatProductMessage({ id: 'product.details', defaultMessage: 'Details' })}
                    details={
                      product.details?.length &&
                      product.details.length > 0 && (
                        <div className="mb-4">
                          <ul role="list">
                            {product.details.map((detail) => (
                              <div key={detail.name}>
                                {detail.items?.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </div>
                            ))}
                          </ul>
                        </div>
                      )
                    }
                  />
                  <TabItem
                    title={formatProductMessage({ id: 'reviews' })}
                    details={
                      <ReviewSection
                        productId={product.productId}
                        reviews={product.reviews}
                        reviewMessage={product.reviewMessage}
                        hasReviewed={product.hasReviewed}
                        hasBought={product.hasBought}
                        reviewStats={product.reviewStats}
                        moderateReviews={product.moderateReviews}
                      />
                    }
                  />
                </div>
                <div className="mb-4 flex items-center gap-4">
                  <FacebookShareButton url={product._url} quote={product.name}>
                    <FacebookIcon />
                  </FacebookShareButton>
                  <LinkedinShareButton url={product._url} title={product.name}>
                    <LinkedinIcon />
                  </LinkedinShareButton>
                  <PinterestShareButton
                    url={product._url}
                    description={product.description}
                    title={product.name}
                    media={variant?.images[selectedImageIndex] ?? variant.images[0]}
                  >
                    <PinterestIcon />
                  </PinterestShareButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </FullPageWidthWrapper>
  );
}

const TabItem = ({ title, details }: { title: string; details: string | React.ReactNode }) => {
  const [tabOpen, setTabOpen] = useState(false);

  return (
    <div className="border-b border-[#e7e7e7]">
      <button
        onClick={() => {
          setTabOpen(!tabOpen);
        }}
        className="mb-4 flex w-full items-center justify-between text-left"
      >
        <span className="text-base font-medium text-black">{title}</span>
        <span className="text-xl font-medium text-black">{tabOpen ? ' - ' : ' + '}</span>
      </button>
      <div className={`${!tabOpen && 'hidden'}`}>
        {typeof details === 'string' ? (
          <p className="mb-4 text-base font-medium text-black">{details}</p>
        ) : (
          <span className="mb-4 text-base font-medium text-black">{details}</span>
        )}
      </div>
    </div>
  );
};
